import { ReactNode, useMemo } from "react";

import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";

import NotAuthorizedTenantAlert from "./NotAuthorizedTenantAlert";

export interface AuthorizedTenantElementProps {
  /** List of tenants user must belong to (all). */
  tenantIds?: Array<string | null | undefined> | null;
  /** List of tenants user must belong to (at least one from the list). */
  tenantIdsAny?: Array<string | null | undefined> | null;
  /** Show alert if user doesn't belong to required tenants. */
  withAlert?: boolean;
  alert?: ReactNode;
  fallback?: ReactNode;
  children: ReactNode;
}

/** Renders children only if current user logged in and belong to the required tenant.
 * NB: previously it checked that user just belong to the tenant what was incorrect.
 */
export default function AuthorizedTenantElement({
  tenantIds,
  tenantIdsAny,
  withAlert,
  alert,
  fallback,
  children,
}: AuthorizedTenantElementProps): ReactNode {
  const currentTenant = useCurrentTenant();

  // only use the current tenant ID instead of all user's tenants
  const currentTenantId = currentTenant?.id;

  const tenantIdsComputed = useMemo(() => tenantIds?.filter(Boolean).map((x) => x!), [tenantIds]);
  const tenantIdsAnyComputed = useMemo(
    () => tenantIdsAny?.filter(Boolean).map((x) => x!),
    [tenantIdsAny],
  );

  const hasAll = useMemo(
    () =>
      !tenantIdsComputed ||
      (!!currentTenantId && tenantIdsComputed.every((id) => id === currentTenantId)),
    [tenantIdsComputed, currentTenantId],
  );
  const hasAny = useMemo(
    () =>
      !tenantIdsAnyComputed ||
      (!!currentTenantId && tenantIdsAnyComputed.some((id) => id === currentTenantId)),
    [tenantIdsAnyComputed, currentTenantId],
  );
  const has = hasAll && hasAny;

  if (!has) {
    if (withAlert || alert) {
      return alert || <NotAuthorizedTenantAlert sx={{ my: 1 }} />;
    }
    return fallback || null;
  }

  return children;
}
